// Banner — inline status/tone callout row placed inside a card or section.
// i18n-clean: the message (children) and optional action arrive via props;
// the primitive imports only from `./` (ui), never a feature.
import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface BannerProps {
  tone: 'info' | 'success' | 'warn' | 'error';
  icon?: IconName;
  children: ReactNode;
  action?: ReactNode;
}

export function Banner({ tone, icon, children, action }: BannerProps) {
  // tone values map 1:1 to the `--status-{family}` token families, which expose
  // only -fg/-bg; the family fg doubles as the border color for the callout.
  const fg = `var(--status-${tone}-fg)`;
  const bg = `var(--status-${tone}-bg)`;
  // Positive/neutral tones announce politely; warn/error are assertive alerts.
  const role = tone === 'warn' || tone === 'error' ? 'alert' : 'status';

  const row: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '11px 14px',
    borderRadius: 'var(--r-2)',
    background: bg,
    border: `1px solid ${fg}`,
  };

  return (
    <div role={role} style={row}>
      {icon && <Icon name={icon} size={14} style={{ color: fg, flex: 'none' }} />}
      <div style={{ fontSize: 'var(--fz-13)', color: fg }}>{children}</div>
      {action && (
        <>
          <span style={{ flex: 1 }} />
          {action}
        </>
      )}
    </div>
  );
}
